import { ScheduleChannel } from '@dadohr/shared/domainTypes/schedules';
import {
  Button,
  ButtonProps,
  Divider,
  Menu,
  MenuItem
} from '@material-ui/core';
import { Send } from 'lucide-react';
import React, { useState } from 'react';
import { ChannelIcon } from '../../features/Experiences/components/Channels';
import useIsMounted from '../../hooks/useIsMounted';
import { useUnsafeTranslation } from '../../i18next';
import { FlexContainer } from '../../layout/Flex';
import { channelToKey, channelToString } from '../../services/channels';
import { ButtonLoader } from '../ButtonWithPromise';

type ButtonWithDropdownOptionProps = {
  pending: React.ReactNode;
  options: ScheduleChannel[];
  allOptionLabel: string;
  onSelect: (channels: ScheduleChannel[]) => Promise<void>;
} & Omit<ButtonProps, 'onClick' | 'onSelect'>;

export const ButtonWithDropdownOption = (
  props: ButtonWithDropdownOptionProps
) => {
  const { t } = useUnsafeTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMounted = useIsMounted();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const {
    options,
    allOptionLabel,
    pending,
    disabled,
    children,
    onSelect,
    ...other
  } = props;

  const done = () => isMounted.current && setLoading(false);

  const handleToggleMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    setOpen((prev) => !prev);
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };

  const handleSelect = async (option: ScheduleChannel[]) => {
    setLoading(true);
    await onSelect(option).then(done, done);
    handleMenuClose();
  };

  return (
    <>
      <Button
        {...other}
        onClick={handleToggleMenu}
        disabled={disabled || loading}
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        {loading ? (
          <>
            <ButtonLoader /> {pending}
          </>
        ) : (
          children
        )}
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        {options.map((ch) => {
          const key = channelToKey(ch);
          const channelName = channelToString(ch, t);
          return (
            <MenuItem key={key} onClick={() => handleSelect([ch])}>
              <FlexContainer>
                <ChannelIcon channel={ch} size={18} />
                {channelName}
              </FlexContainer>
            </MenuItem>
          );
        })}
        {options.length > 1 && (
          <div>
            <Divider light />
            <MenuItem onClick={() => handleSelect(options)}>
              <FlexContainer>
                <Send size={18} />
                {allOptionLabel}
              </FlexContainer>
            </MenuItem>
          </div>
        )}
      </Menu>
    </>
  );
};
